import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import './ModelViewer.css';
import { getBoneInfo } from './boneNames';
import { isIOS } from './deviceDetect';
import { useFocusMeshOnLoad } from './useFocusMeshOnLoad';
import { useMeshSelection } from './useMeshSelection';
import ModelSearchBar from './ModelSearchBar';
import { extractInternals, FILTER_KEYWORDS, HIGHLIGHT_COLOR } from './modelViewerUtils';

function SkeletonFull({ modelSrc }) {
  const navigate = useNavigate();
  const modelRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const originalMaterialsRef = useRef(new Map());
  const highlightedMeshRef = useRef(null);
  const originalRenderOrderRef = useRef(0);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());

  const forceRender = useCallback(() => {
    const mv = modelRef.current;
    if (!mv) return;
    if (mv.requestUpdate) { mv.requestUpdate(); return; }
    try {
      const expAttr = mv.getAttribute('exposure') || '1.1';
      const exp = parseFloat(expAttr);
      mv.setAttribute('exposure', (exp + 0.000001).toString());
      requestAnimationFrame(() => {
        if (modelRef.current) modelRef.current.setAttribute('exposure', expAttr);
      });
    } catch (e) { }
  }, []);

  const highlightMesh = useCallback((mesh) => {
    if (!mesh || !mesh.material) return;

    if (!originalMaterialsRef.current.has(mesh.uuid)) {
      const orig = mesh.material;
      let cloned;
      if (Array.isArray(orig)) {
        cloned = orig.map(m => (m && typeof m.clone === 'function') ? m.clone() : null).filter(Boolean);
      } else if (orig && typeof orig.clone === 'function') {
        cloned = orig.clone();
      }
      if (cloned) {
        mesh.material = cloned;
        const mats = Array.isArray(cloned) ? cloned : [cloned];
        const props = mats.map(m => ({
          color: (m.color && typeof m.color.clone === 'function') ? m.color.clone() : new THREE.Color(0xffffff),
          opacity: m.opacity ?? 1,
          transparent: !!m.transparent,
          depthWrite: m.depthWrite ?? true,
          emissive: (m.emissive && typeof m.emissive.clone === 'function') ? m.emissive.clone() : new THREE.Color(0x000000),
          emissiveIntensity: m.emissiveIntensity ?? 1,
        }));
        originalMaterialsRef.current.set(mesh.uuid, props);
      }
    }

    originalRenderOrderRef.current = mesh.renderOrder;

    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach(m => {
      if (!m) return;
      if (m.color) m.color.set(HIGHLIGHT_COLOR);
      m.opacity = 0.75;
      m.transparent = true;
      m.depthWrite = false;
      if (m.emissive) {
        m.emissive.set(0x000000);
        m.emissiveIntensity = 0;
      }
    });
    mesh.renderOrder = 999;

    highlightedMeshRef.current = mesh;
    forceRender();
  }, [forceRender]);

  const resetHighlight = useCallback(() => {
    const mesh = highlightedMeshRef.current;
    if (!mesh || !mesh.isMesh) return;
    const props = originalMaterialsRef.current.get(mesh.uuid);
    if (props) {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((m, i) => {
        const p = props[i];
        if (m && p) {
          if (m.color && p.color) m.color.set(p.color);
          m.opacity = p.opacity;
          m.transparent = p.transparent;
          m.depthWrite = p.depthWrite;
          if (m.emissive && p.emissive) m.emissive.set(p.emissive);
          if (p.emissiveIntensity !== undefined) m.emissiveIntensity = p.emissiveIntensity;
        }
      });
    }
    mesh.renderOrder = originalRenderOrderRef.current;
    highlightedMeshRef.current = null;
    forceRender();
  }, [forceRender]);

  const { selectedPart, hotspotPosition, screenSide, hotspotKey, applySelection, closeLabel, setScreenSide } =
    useMeshSelection({ infoLookup: getBoneInfo, highlightMesh, resetHighlight });

  const onFound = useCallback((mesh, center, normal) => {
    applySelection(mesh, center, normal);
  }, [applySelection]);

  const { focusMeshIfPending, focusMesh } = useFocusMeshOnLoad({ modelRef, onFound });

  useEffect(() => {
    const mv = modelRef.current;
    if (!mv) return;

    const handleLoad = () => {
      setIsLoading(false);
      setLoadError(false);
      const { scene, camera } = extractInternals(mv);
      if (scene) sceneRef.current = scene;
      if (camera) cameraRef.current = camera;
      focusMeshIfPending();
    };

    const handleProgress = (e) => {
      if (e.detail?.totalProgress === 1) setIsLoading(false);
    };

    const handleError = () => {
      setIsLoading(false);
      setLoadError(true);
    };

    mv.addEventListener('load', handleLoad);
    mv.addEventListener('progress', handleProgress);
    mv.addEventListener('error', handleError);
    if (mv.loaded) handleLoad();

    const timeout = setTimeout(() => setIsLoading(false), 15000);

    return () => {
      mv.removeEventListener('load', handleLoad);
      mv.removeEventListener('progress', handleProgress);
      mv.removeEventListener('error', handleError);
      clearTimeout(timeout);
    };
  }, [focusMeshIfPending]);

  const handleModelClick = useCallback((event) => {
    const mv = modelRef.current;
    if (!mv) return;

    const rect = mv.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const hit = mv.positionAndNormalFromPoint(x, y);
    if (!hit) {
      closeLabel();
      return;
    }

    let identifiedMesh = null;
    let identifiedName = null;

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    if (scene && camera) {
      const ndcX = (x / rect.width) * 2 - 1;
      const ndcY = -(y / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
      const intersects = raycasterRef.current.intersectObjects(scene.children, true);
      const hitResult = intersects.find(i => {
        const obj = i.object;
        if (!obj.isMesh) return false;
        const name = (obj.name || '').toLowerCase();
        const matName = (obj.material?.name || '').toLowerCase();
        return !FILTER_KEYWORDS.some(f => name.includes(f) || matName.includes(f));
      });
      if (hitResult) {
        identifiedMesh = hitResult.object;
        identifiedName = hitResult.object.name || hitResult.object.material?.name || null;
      }
    }

    if (!identifiedName) {
      const mvMat = mv.materialFromPoint(x, y);
      if (mvMat && mvMat.name) identifiedName = mvMat.name;
    }

    if (identifiedName && identifiedName !== 'Unknown') {
      applySelection(identifiedMesh, hit.position, hit.normal);
    } else {
      closeLabel();
    }
  }, [applySelection, closeLabel]);

  const closeLabelInner = useCallback(() => {
    closeLabel();
  }, [closeLabel]);

  useEffect(() => {
    if (!selectedPart || !hotspotPosition) return;

    const mv = modelRef.current;
    if (!mv) return;

    let isRunning = false;
    const worldPos = new THREE.Vector3();
    const posParts = hotspotPosition.position.replace(/m/g, '').split(' ').map(parseFloat);
    worldPos.set(posParts[0], posParts[1], posParts[2]);

    const checkSide = () => {
      if (isRunning) return;
      isRunning = true;

      requestAnimationFrame(() => {
        try {
          const camera = cameraRef.current;
          if (camera && mv) {
            const ndc = worldPos.clone().project(camera);
            const rect = mv.getBoundingClientRect();
            const screenX = (ndc.x + 1) / 2 * rect.width;
            const sideNow = screenX < (rect.width / 2) ? 'left' : 'right';
            setScreenSide(prev => (prev !== sideNow ? sideNow : prev));
          }
        } catch (e) { }
        isRunning = false;
      });
    };

    mv.addEventListener('camera-change', checkSide);
    return () => mv.removeEventListener('camera-change', checkSide);
  }, [selectedPart, hotspotPosition]);

  return (
    <div className="model-viewer-page skeleton-page skeleton-surface-click">
      <button onClick={() => navigate('/menu')} className="back-btn">
        ← Back to Menu
      </button>

      <ModelSearchBar currentSystemId="skeleton" onFocusMesh={focusMesh} />

      {isLoading && (
        <div className="skeleton-mv-loading">
          <div className="skeleton-loading-spinner"></div>
          <p>Loading 3D Skeleton...</p>
        </div>
      )}

      {loadError && (
        <div className="skeleton-mv-loading">
          <p>Unable to load 3D model</p>
        </div>
      )}

      <div className="skeleton-hint">
        Tap on bones to identify them
      </div>

      <model-viewer
        ref={modelRef}
        id="skeleton-viewer"
        src={modelSrc}
        ios-src="/models/skeleton.usdz"
        alt="3D Human Skeleton"
        ar
        ar-modes="webxr"
        camera-controls
        touch-action="none"
        interaction-prompt="auto"
        loading="eager"
        reveal="auto"
        shadow-intensity="0"
        exposure="1.1"
        environment-image="neutral"
        camera-orbit="0deg 75deg 2.5m"
        min-camera-orbit="auto auto 0.05m"
        max-camera-orbit="auto auto 10m"
        field-of-view="45deg"
        quick-look-browsers="safari chrome"
        minimum-render-scale="0.5"
        power-preference="low-power"
        onClick={handleModelClick}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#ffffff',
          '--progress-bar-color': '#00E5FF',
          '--progress-bar-height': '4px',
          cursor: 'var(--cursor-pointer)',
        }}
      >
        {selectedPart && hotspotPosition && (
          <div
            key={hotspotKey}
            className={`skeleton-leader-hotspot ${screenSide}`}
            slot="hotspot-bone"
            data-position={hotspotPosition.position}
            data-normal={hotspotPosition.normal}
          >
            <div className="skeleton-leader-dot"></div>
            <div className="skeleton-leader-line"></div>
            <div className="skeleton-leader-label">
              <div className="skeleton-leader-content">
                <span className="skeleton-leader-english">{selectedPart.english}</span>
                <span className="skeleton-leader-latin">{selectedPart.latin}</span>
              </div>
              <button className="skeleton-leader-close" onClick={closeLabelInner}>✕</button>
            </div>
          </div>
        )}
      </model-viewer>
    </div>
  );
}

function Skeleton() {
  if (isIOS) {
    return <SkeletonFull modelSrc="/models/skeleton.ios.glb" />;
  }
  return <SkeletonFull modelSrc="/models/skeleton.glb" />;
}

export default Skeleton;
